import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchPermissionMatrix, updateRolePermission } from '@/features/permissions/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/permission_management.html — the resource.action permission
 * matrix (rows = permissions, columns = roles). Administrator's column is
 * always checked+disabled, same as the backend (`NewUser.has_permission`
 * short-circuits true for admins regardless of role_permissions rows, and
 * `update_role_permission` rejects edits to the Administrator role outright).
 */
export default function PermissionManagementPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['permission-matrix'],
    queryFn: fetchPermissionMatrix,
  })
  const [search, setSearch] = useState('')
  const [pendingError, setPendingError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: ({ roleId, permissionId, granted }: { roleId: number; permissionId: number; granted: boolean }) =>
      updateRolePermission(roleId, permissionId, granted),
    onMutate: async ({ roleId, permissionId, granted }) => {
      setPendingError(null)
      await queryClient.cancelQueries({ queryKey: ['permission-matrix'] })
      const previous = queryClient.getQueryData(['permission-matrix'])
      queryClient.setQueryData(['permission-matrix'], (old: typeof data) => {
        if (!old) return old
        const current = new Set(old.granted_by_role[String(roleId)] ?? [])
        if (granted) current.add(permissionId)
        else current.delete(permissionId)
        return {
          ...old,
          granted_by_role: { ...old.granted_by_role, [String(roleId)]: Array.from(current) },
        }
      })
      return { previous }
    },
    onError: (err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(['permission-matrix'], context.previous)
      setPendingError(err instanceof Error ? err.message : 'Failed to update permission.')
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['permission-matrix'] }),
  })

  const filteredPermissions = useMemo(() => {
    if (!data) return []
    if (!search) return data.permissions
    const q = search.toLowerCase()
    return data.permissions.filter(
      (p) => p.permission_key.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q),
    )
  }, [data, search])

  if (isLoading) return <LoadingState label="Loading permissions…" />
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Failed to load permissions.'}
        onRetry={() => refetch()}
      />
    )
  }
  if (!data) return null

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-ink">Users Permissions</h1>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search permissions…"
        className="mb-4 w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
      />

      {pendingError && (
        <div className="mb-3">
          <ErrorState message={pendingError} />
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
              <th className="px-4 py-3.5 font-semibold">Permission</th>
              {data.roles.map((role) => (
                <th key={role.id} className="px-4 py-2 text-center font-semibold">
                  {role.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredPermissions.map((permission) => (
              <tr key={permission.id} className="border-b border-nav-line last:border-0">
                <td className="px-4 py-3.5 text-ink">
                  {permission.permission_key}
                  {permission.description && (
                    <div className="text-xs text-text-secondary">{permission.description}</div>
                  )}
                </td>
                {data.roles.map((role) => {
                  const isAdmin = role.name === 'Administrator'
                  const granted = (data.granted_by_role[String(role.id)] ?? []).includes(permission.id)
                  return (
                    <td key={role.id} className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isAdmin || granted}
                        disabled={isAdmin || mutation.isPending}
                        onChange={(e) =>
                          mutation.mutate({ roleId: role.id, permissionId: permission.id, granted: e.target.checked })
                        }
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
